/**
 * "What changed" + Undo after an approved CoPilot change (CU-14zefhy1qnh, item 8; twin of the RouteMagic one).
 * The server reads the fields before each write and returns them as `changes` on the events response of a completed
 * approved turn. Undo asks the server for a revert plan, which the user approves like any other plan.
 * Pure helpers live here so they can be unit tested without the component.
 */
import {isPlanExpired, PlanCard} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-plan';

export interface FieldChange {
    before: any;
    after: any;
}

export interface ChangeEntry {
    entity: string;
    id: string;
    label: string;
    fields: {[name: string]: FieldChange};
    /** the turn created this record (undo proposes deleting it) */
    created?: boolean;
}

/** idle = Undo offered · loading = request in flight · proposed = revert plan shown · undone = already undone · unavailable = cannot be undone */
export type UndoState = 'idle' | 'loading' | 'proposed' | 'undone' | 'unavailable';

export interface ChangesCard {
    taskId: string;
    items: ChangeEntry[];
    /** epoch ms when the change ran (the server allows undo for 24 h) */
    at: number;
    undo: UndoState;
    /** why undo is not offered / failed */
    note?: string;
}

export const UNDO_WINDOW_MS = 24 * 60 * 60 * 1000;
export const MAX_CHANGE_ITEMS = 50;

const CHANGES_BLOCK = /```(?:hm|rm)-changes[^\n]*\n[\s\S]*?```/g;

/** Removes any fenced changes block from the reply text (the server strips it too; this guards raw results). */
export function stripChangesBlock(reply: string): string {
    if (!reply || !/```(?:hm|rm)-changes/.test(reply)) {
        return reply;
    }
    return reply
        .replace(CHANGES_BLOCK, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

function str(v: any): string {
    return v === null || v === undefined ? '' : String(v).trim();
}

/** Normalises the server's `changes` list; malformed items are skipped. Empty array when nothing usable. */
export function toChanges(raw: any): ChangeEntry[] {
    if (!Array.isArray(raw)) {
        return [];
    }
    const out: ChangeEntry[] = [];
    for (const item of raw) {
        if (!item || typeof item !== 'object') {
            continue;
        }
        const entity = str(item.entity);
        const id = str(item.id);
        if (!entity && !id) {
            continue;
        }
        const fields: {[name: string]: FieldChange} = {};
        if (item.fields && typeof item.fields === 'object' && !Array.isArray(item.fields)) {
            Object.keys(item.fields).forEach((name) => {
                const f = item.fields[name];
                if (f && typeof f === 'object' && !Array.isArray(f)) {
                    fields[name] = {before: f.before ?? null, after: f.after ?? null};
                }
            });
        }
        const entry: ChangeEntry = {entity, id, label: str(item.label) || [entity, id].filter((s) => !!s).join(' '), fields};
        if (item.created === true) {
            entry.created = true;
        }
        if (entry.created || Object.keys(fields).length) {
            out.push(entry);
        }
        if (out.length >= MAX_CHANGE_ITEMS) {
            break;
        }
    }
    return out;
}

/** A card for an approved turn's reply; null when the server sent no changes. */
export function toChangesCard(raw: any, taskId: string, now: number = Date.now()): ChangesCard | null {
    const items = toChanges(raw);
    if (!items.length || !taskId) {
        return null;
    }
    return {taskId: String(taskId), items, at: now, undo: 'idle'};
}

/** Short display of a value: empty shows as "(empty)", objects as compact JSON, long text cut at 80 chars. */
export function displayValue(value: any): string {
    if (value === null || value === undefined || value === '') {
        return '(empty)';
    }
    let s: string;
    if (typeof value === 'object') {
        try {
            s = JSON.stringify(value);
        } catch (e) {
            s = String(value);
        }
    } else {
        s = String(value);
    }
    return s.length > 80 ? s.substring(0, 79) + '…' : s;
}

/** One line per changed field: "Run R-0005 · driver: Bob → Ann"; created records: "Order 12 · created". */
export function changeLines(items: ChangeEntry[]): string[] {
    const lines: string[] = [];
    (items || []).forEach((c) => {
        const head = c.label || [c.entity, c.id].filter((s) => !!s).join(' ');
        if (c.created) {
            lines.push(`${head} · created`);
            return;
        }
        Object.keys(c.fields || {}).forEach((name) => {
            const f = c.fields[name];
            lines.push(`${head} · ${name}: ${displayValue(f.before)} → ${displayValue(f.after)}`);
        });
    });
    return lines;
}

/** Undo is offered while idle, within 24 h, and when no turn runs in the session. */
export function canUndo(card: ChangesCard | null | undefined, sessionBusy: boolean, now: number = Date.now()): boolean {
    return !!card && card.undo === 'idle' && !sessionBusy && !isUndoExpired(card, now);
}

export function isUndoExpired(card: ChangesCard, now: number = Date.now()): boolean {
    return !!card.at && now - card.at > UNDO_WINDOW_MS;
}

/** The revert plan from the undo response: `{pendingPlan: {...}}` or the plan object itself; null when neither. */
export function undoPlanOf(response: any): any {
    if (!response || typeof response !== 'object') {
        return null;
    }
    if (response.pendingPlan && typeof response.pendingPlan === 'object') {
        return response.pendingPlan;
    }
    return response.id !== undefined && response.id !== null ? response : null;
}

/** The server's own reason from an error body: `{error}` (CoPilot API), problem+json `detail`, or a readable `message`. */
export function serverReason(error: any): string {
    const body = error?.error;
    if (typeof body === 'string') {
        const s = body.trim();
        return s.startsWith('{') || s.startsWith('<') ? '' : s;
    }
    if (!body || typeof body !== 'object') {
        return '';
    }
    const found = [body.error, body.detail, body.message].find((v) => typeof v === 'string' && v.trim() && !/^error\.[\w.]+$/.test(v.trim()));
    return found ? found.trim() : '';
}

/**
 * Final undo state for an error (lead decision: 403/404/409/410 all hide Undo and show the server's reason, e.g.
 * "expired" or "already undone"); anything else is retryable (null).
 */
export function undoErrorState(httpStatus: number | undefined): {state: UndoState; note: string} | null {
    if (httpStatus === 409) {
        return {state: 'undone', note: 'Already undone'};
    }
    if (httpStatus === 403 || httpStatus === 404 || httpStatus === 410) {
        return {state: 'unavailable', note: "This change can't be undone any more (older than 24 hours or not yours)"};
    }
    return null;
}

/** `undoStatus` on the events response: "undone" / "expired" close the card; "available" (or absent) leaves Undo on. */
export function applyUndoStatus(card: ChangesCard, undoStatus: any): ChangesCard {
    if (undoStatus === 'undone') {
        return {...card, undo: 'undone', note: 'Already undone'};
    }
    if (undoStatus === 'expired') {
        return {...card, undo: 'unavailable', note: "Can't be undone any more (older than 24 hours)"};
    }
    return card;
}

/** The approved turn a revert plan belongs to (`undo.<taskId>`), or null for an ordinary plan. */
export function undoTaskIdOf(planId: string | null | undefined): string | null {
    const id = String(planId || '');
    return id.startsWith('undo.') && id.length > 5 ? id.substring(5) : null;
}

/** Label under the list when Undo is not offered. */
export function undoStateLabel(card: ChangesCard, now: number = Date.now()): string {
    if (card.undo === 'idle' && isUndoExpired(card, now)) {
        return "Can't be undone any more (older than 24 hours)";
    }
    switch (card.undo) {
        case 'loading':
            return 'Preparing the undo…';
        case 'proposed':
            return 'Undo proposed below — approve it to restore the old values';
        case 'undone':
            return card.note || 'Already undone';
        case 'unavailable':
            return card.note || "This change can't be undone";
        default:
            return '';
    }
}

/** After a reload: a request in flight goes back to idle (the server answers 409 if it was already used). */
export function restoreChangesCard(card: ChangesCard): ChangesCard {
    return card.undo === 'loading' ? {...card, undo: 'idle'} : card;
}

/**
 * Whether the revert plan behind a 'proposed' card can still move on: pending and not past its TTL, or a request /
 * the approved revert turn is in flight. `afterReload` = no turn of this page runs any more, so an 'approved' plan whose
 * turn never finished here no longer holds the card.
 */
export function undoProposalLive(plan: PlanCard | null | undefined, afterReload = false, now: number = Date.now()): boolean {
    if (!plan) {
        return false;
    }
    if (plan.state === 'pending') {
        return !isPlanExpired(plan, now);
    }
    return !afterReload && (plan.state === 'approving' || plan.state === 'cancelling' || plan.state === 'approved');
}

/** The revert plan card (`undo.<taskId>`) of a "What changed" card, or null. */
export function undoPlanFor(card: ChangesCard | null | undefined, messages: {plan?: PlanCard}[]): PlanCard | null {
    if (!card) {
        return null;
    }
    const id = 'undo.' + card.taskId;
    const found = (messages || []).find((m) => m?.plan?.id === id);
    return found ? found.plan : null;
}

/**
 * A 'proposed' card whose revert plan expired, was cancelled or is gone shows as idle (Undo offered again; the
 * server answers 409 "already undone" if the revert did run). Other cards are returned as they are.
 */
export function effectiveUndoCard(card: ChangesCard, plan: PlanCard | null | undefined, afterReload = false, now: number = Date.now()): ChangesCard {
    if (card && card.undo === 'proposed' && !undoProposalLive(plan, afterReload, now)) {
        return {...card, undo: 'idle', note: undefined};
    }
    return card;
}

/** After a reload (plan cards already restored): settle every 'proposed' card whose revert plan is no longer live. */
export function settleProposedUndos(messages: {changes?: ChangesCard; plan?: PlanCard}[], now: number = Date.now()): void {
    (messages || []).forEach((m) => {
        if (m?.changes) {
            m.changes = effectiveUndoCard(m.changes, undoPlanFor(m.changes, messages), true, now);
        }
    });
}
